//@flow

import React, {useState} from 'react';
import {Typography, makeStyles} from '@material-ui/core';

import CustomModal from '../../../../../elements/CustomModal/CustomModal';
import {FormProvider, useForm} from 'react-hook-form';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';

import MessageField from './MessageField';
import AttachmentField from './AttachmentField';
import {colors} from '../../../../../assets/styles/colors';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {QUICK_REPLY_FIELDS} from './constants';
import {useQuickRepliesActions} from '../../utils';

import InlineLoader from '../../../../../elements/LoadingSpinner/InlineLoader';

import type {QuickReply} from '../../../../../entities';
import type {QuickReplyField} from './constants';
import {getImageUrl} from '../../../../../utils/image_provider';
import Toast from '../../../../../elements/Toast';
import {useIsFeatureInTier} from '../../../../../utils/component';
import TierRestrictionModal from '../../../../../elements/TierRestrictionModal/TierRestrictionModal';
import {useEffect} from 'react';

type Props = {
  onClose: () => void,
  quickReply?: QuickReply,
};
const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: '520px',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    position: 'relative',
  },
  loader: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    zIndex: 1,
    background: 'rgba(255,255,255,.5)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '.5rem',
    width: '100%',
  },
  title: {
    color: colors.grey900,
    fontWeight: 600,
    fontSize: '1.25rem',
    textAlign: 'center',
  },
  subtitle: {
    color: colors.grey700,
    fontSize: '.875rem',
    textAlign: 'start',
  },
  content: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '.75rem',
  },
  submitButton: {
    maxWidth: '200px',
    marginTop: '1rem',
    position: 'sticky',
    bottom: '1rem',
    margin: '0 auto',
  },
}));

export default function RepliesForm({onClose, quickReply}: Props) {
  const classes = useStyles();

  const hasQuickReplyFeature = useIsFeatureInTier('TIENDACHAT_QUICK_REPLIES');

  const [tierModalOpen, setTierModalOpen] = useState(false);

  const methods = useForm({
    defaultValues: {
      [QUICK_REPLY_FIELDS.name]: quickReply && quickReply.displayName,
      [QUICK_REPLY_FIELDS.message]: quickReply && quickReply.message,
      [QUICK_REPLY_FIELDS.fileId]: quickReply && quickReply.fileId,
    },
  });
  const {
    createQuickReply,
    loading,
    editQuickReply,
    error,
  } = useQuickRepliesActions();

  const {register, errors} = methods;

  useEffect(() => {
    register(QUICK_REPLY_FIELDS.fileId);
    register(QUICK_REPLY_FIELDS.fileName);
  }, [register]);

  const handleSubmitSuccess = () => {
    onClose();
  };

  const handleSubmit = (data: QuickReplyField) => {
    if (!hasQuickReplyFeature) {
      setTierModalOpen(true);
      return;
    }

    if (quickReply) {
      const isDirty =
        data.message !== quickReply.message ||
        data.name !== quickReply.normalizedName ||
        !!data.files;
      if (!isDirty) return handleSubmitSuccess();

      editQuickReply(quickReply.id, data)
        .then(() => {
          handleSubmitSuccess();
        })
        .catch(() => {});
      return;
    }
    createQuickReply(data).then(() => {
      handleSubmitSuccess();
    });
  };

  return (
    <>
      <CustomModal open handleCloseModal={onClose}>
        <FormProvider {...methods}>
          <div className={classes.root}>
            <InlineLoader loading={loading} className={classes.loader} />
            <header className={classes.header}>
              <Typography className={classes.title}>
                Nueva respuesta rápida
              </Typography>
              <Typography className={classes.subtitle}>
                Las respuestas te ayudarán a darle responder rápidamente a las
                preguntas de tus clientes. Puedes usar estos templates usando el
                botón de “Respuestas rápidas”.
              </Typography>
            </header>
            <div className={classes.content}>
              <CustomTextField
                placeholder="nombre"
                inputRef={register({required: true})}
                name={QUICK_REPLY_FIELDS.name}
                error={!!errors[QUICK_REPLY_FIELDS.name]}
                helperText={
                  errors[QUICK_REPLY_FIELDS.name] &&
                  'El nombre de la respuesta es requerido'
                }
              />
              <MessageField />
              <AttachmentField
                savedImage={
                  quickReply &&
                  quickReply.fileId &&
                  quickReply.fileType === 'IMAGE'
                    ? getImageUrl(quickReply.fileId)
                    : undefined
                }
              />
              <CustomButton
                variant="contained"
                color="primary"
                className={classes.submitButton}
                fullWidth
                disabled={loading}
                onClick={methods.handleSubmit(handleSubmit)}
              >
                {quickReply ? 'Guardar' : 'Crear'}
              </CustomButton>
            </div>
          </div>
        </FormProvider>
        <Toast open={!!error} severity="error" closeDelay={4000}>
          {error}
        </Toast>
      </CustomModal>
      <TierRestrictionModal
        open={tierModalOpen && !hasQuickReplyFeature}
        handleClose={() => {
          setTierModalOpen(false);
        }}
      />
    </>
  );
}
